/**
 * Hot rated reads bounded to the partitions a call needs (gateway, platform,
 * warm-cold-manager). Two things, and nothing else:
 *
 *   - an ops aggregate (and a request list) whose rated subselect used to
 *     copy the whole hot table gets an `event_at` window. A leading run of
 *     cold Shanghai days is cut off that window, so a 90-day call does not
 *     hash verdicts the usage scan already left out;
 *   - `min(rated_at)` walks partitions from the oldest `event_at` bound.
 *     A partition's rows are not treated as earlier than its lower bound
 *     minus one day (the same shoulder the money join allows between
 *     `event_at` and `created_at`; the measured −20.07s is an observation,
 *     not a bound). The walk stops when the next bound, minus that day, is
 *     not earlier than the minimum already found. A late verdict
 *     (`rated_at = now()` on an old `event_at`) does not hide an earlier
 *     verdict in a newer partition.
 *
 * Gateway still does not read the cold dataset. The bound only stops a hot
 * statement from opening partitions the call does not need.
 */
const SHANGHAI_OFFSET_MS = 8 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/u;

/**
 * How much earlier `rated_at` may be than `event_at` before the floor walk
 * may skip a later partition. One day, the same shoulder as the money join
 * (`event_at` within `created_at` ± 1 day). The fleet's measured gap of
 * −20.07s is not a guarantee.
 */
export const RATED_AT_CLOCK_SKEW_MS = 86_400_000;

export function shanghaiDayOfMs(ms: number): string {
  return new Date(ms + SHANGHAI_OFFSET_MS).toISOString().slice(0, 10);
}

export function shanghaiDayStartMs(day: string): number {
  return Date.parse(`${day}T00:00:00.000+08:00`);
}

export interface RatedJoinWindow {
  readonly fromIso: string;
  readonly toIso: string;
}

/**
 * The rated subselect's `[from, to)` once leading cold Shanghai days are
 * removed.
 *
 * Returns the caller's own `fromIso` string when the window does not start
 * on a cold day, so a statement that already interpolates that string does
 * not change. Returns null when every instant of the window is a cold day:
 * the caller must not open the hot rated table for it.
 */
export function clipRatedJoinWindow(
  window: RatedJoinWindow,
  coldDays: readonly string[] | undefined,
): RatedJoinWindow | null {
  const fromMs = Date.parse(window.fromIso);
  const toMs = Date.parse(window.toIso);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || !(fromMs < toMs)) return null;
  if (coldDays === undefined || coldDays.length === 0) return window;
  const cold = new Set(coldDays.filter((day) => DAY_RE.test(day)));
  if (cold.size === 0) return window;
  let cursor = fromMs;
  for (let step = 0; step < 400 && cursor < toMs; step += 1) {
    const day = shanghaiDayOfMs(cursor);
    if (!cold.has(day)) {
      return cursor === fromMs ? window : { fromIso: new Date(cursor).toISOString(), toIso: window.toIso };
    }
    const next = shanghaiDayStartMs(day) + DAY_MS;
    if (!(next > cursor)) return window;
    cursor = next;
  }
  return cursor < toMs ? { fromIso: new Date(cursor).toISOString(), toIso: window.toIso } : null;
}

/**
 * Window passed to a rated subselect: cold days clipped (`undefined` stays
 * absent). A fully cold window becomes an empty range at `to`
 * (`fromIso === toIso`). The rated subselect turns that sentinel into
 * `WHERE false`, so it does not open the partition that contains `to` (hist,
 * when `to` is before the first daily bound).
 */
export function ratedJoinWindowForBound(
  current: RatedJoinWindow | undefined,
  coldDays: readonly string[] | undefined,
): RatedJoinWindow | undefined {
  if (current === undefined) return undefined;
  return clipRatedJoinWindow(current, coldDays) ?? { fromIso: current.toIso, toIso: current.toIso };
}

const SCHEMA_NAME = /^[a-z_][a-z0-9_]*$/u;

/** Schema of `usage_rated_events`. Production is `public`; a test passes its fixture schema. */
export function ratedPartitionsSql(schema = 'public'): string {
  if (!SCHEMA_NAME.test(schema)) throw new Error(`rated floor: unexpected schema ${schema}`);
  return `SELECT c.relname AS relname,
            pg_get_expr(c.relpartbound, c.oid) AS bound
       FROM pg_catalog.pg_inherits i
       JOIN pg_catalog.pg_class c ON c.oid = i.inhrelid
      WHERE i.inhparent = pg_catalog.to_regclass('${schema}.usage_rated_events')`;
}

/** Production catalog of `public.usage_rated_events`. */
export const RATED_PARTITIONS_SQL = ratedPartitionsSql();

/** A child that cannot be walked: the whole-table aggregate. */
export const RATED_AT_FLOOR_SQL = 'SELECT min(rated_at) AS rated_at FROM usage_rated_events';

/** Heap, one index step, same row as {@link RATED_AT_FLOOR_SQL}. */
export const RATED_AT_FLOOR_LIMIT_SQL = 'SELECT rated_at FROM usage_rated_events ORDER BY rated_at ASC LIMIT 1';

const PARTITION_NAME = /^usage_rated_events(?:_hist|_p_\d{8})$/u;

export interface RatedPartitionBound {
  readonly relname: string;
  readonly bound: string | null;
}

export interface RatedPartitionFloor {
  readonly relname: string;
  /** `-Infinity` for `FROM (MINVALUE)`. */
  readonly lowerMs: number;
}

/**
 * Oldest partition first. `null` when any child cannot be walked safely:
 * a name other than hist / `p_YYYYMMDD` (a DEFAULT partition included), or a
 * FROM bound `Date.parse` cannot read. The caller then uses the parent
 * `min(rated_at)` instead of skipping that child.
 */
export function ratedPartitionFloors(rows: readonly RatedPartitionBound[]): readonly RatedPartitionFloor[] | null {
  const parsed: Array<{ relname: string; minvalue: boolean; lowerMs: number }> = [];
  for (const row of rows) {
    if (!PARTITION_NAME.test(row.relname)) return null;
    const bound = row.bound ?? '';
    const minvalue = /FROM \(MINVALUE\)/u.test(bound);
    if (minvalue) {
      parsed.push({ relname: row.relname, minvalue: true, lowerMs: Number.NEGATIVE_INFINITY });
      continue;
    }
    const from = /FROM \('([^']+)'\)/u.exec(bound)?.[1];
    const parsedFrom = from === undefined ? Number.NaN : Date.parse(from);
    if (!Number.isFinite(parsedFrom)) return null;
    parsed.push({ relname: row.relname, minvalue: false, lowerMs: parsedFrom });
  }
  parsed.sort((left, right) => {
    if (left.minvalue !== right.minvalue) return left.minvalue ? -1 : 1;
    if (left.lowerMs !== right.lowerMs) return left.lowerMs - right.lowerMs;
    return left.relname < right.relname ? -1 : 1;
  });
  return parsed.map((row) => ({ relname: row.relname, lowerMs: row.lowerMs }));
}

export function ratedPartitionFloorOrder(rows: readonly RatedPartitionBound[]): readonly string[] | null {
  const floors = ratedPartitionFloors(rows);
  return floors === null ? null : floors.map((row) => row.relname);
}

/**
 * Stop before the next partition when its lower bound, minus clock skew, is
 * not earlier than the minimum already found. A later partition's `event_at`
 * is at least that bound, so its `rated_at` cannot be earlier than the bound
 * minus the skew.
 */
export function ratedFloorMayStop(
  currentMinMs: number,
  nextLowerMs: number,
  skewMs = RATED_AT_CLOCK_SKEW_MS,
): boolean {
  if (!Number.isFinite(currentMinMs) || !Number.isFinite(nextLowerMs)) return false;
  return nextLowerMs - skewMs >= currentMinMs;
}

/** One index step on a single partition. The name and schema are checked; neither is taken from outside this list. */
export function ratedAtFromOnlyPartitionSql(relname: string, schema = 'public'): string {
  if (!SCHEMA_NAME.test(schema)) throw new Error(`rated floor: unexpected schema ${schema}`);
  if (!PARTITION_NAME.test(relname)) {
    throw new Error(`rated floor: unexpected relation ${relname}`);
  }
  return `SELECT rated_at FROM ONLY ${schema}.${relname} ORDER BY rated_at ASC LIMIT 1`;
}

/**
 * Structural match for a pg executor. Returns are `unknown` so a driver whose
 * `maybeOne` is typed `Promise<object | null>` still assigns.
 */
export interface RatedFloorQuery {
  maybeOne(sql: string, values?: readonly unknown[]): Promise<unknown>;
  many(sql: string, values?: readonly unknown[]): Promise<unknown>;
}

/**
 * Oldest `rated_at` still in the hot table, the same row as
 * {@link RATED_AT_FLOOR_SQL}: partitions from oldest `event_at` bound to
 * newest. Keep the smallest `rated_at`. Stop when the next partition's lower
 * bound minus {@link RATED_AT_CLOCK_SKEW_MS} is not earlier than that
 * minimum: no later row can undercut it. A heap (no partitions) is one index
 * step; a child this walk cannot read falls back to the whole-table min.
 * `schema` defaults to `public`.
 */
export async function readOldestRatedAt(
  db: RatedFloorQuery,
  schema = 'public',
): Promise<Date | string | null> {
  const parts = await db.many(ratedPartitionsSql(schema));
  const order = ratedPartitionFloors(partitionRows(parts));
  if (order === null) {
    return ratedAtOf(await db.maybeOne(RATED_AT_FLOOR_SQL));
  }
  if (order.length === 0) {
    return ratedAtOf(await db.maybeOne(RATED_AT_FLOOR_LIMIT_SQL));
  }
  let best: Date | string | null = null;
  let bestMs = Number.POSITIVE_INFINITY;
  for (let index = 0; index < order.length; index += 1) {
    const part = order[index];
    if (part === undefined) break;
    const at = ratedAtOf(await db.maybeOne(ratedAtFromOnlyPartitionSql(part.relname, schema)));
    if (at != null) {
      const ms = at instanceof Date ? at.getTime() : Date.parse(at);
      if (Number.isFinite(ms) && ms < bestMs) {
        best = at;
        bestMs = ms;
      }
    }
    const next = order[index + 1];
    if (best != null && next !== undefined && ratedFloorMayStop(bestMs, next.lowerMs)) return best;
  }
  return best;
}

function ratedAtOf(row: unknown): Date | string | null {
  if (row === null || typeof row !== 'object' || !('rated_at' in row)) return null;
  const at = row.rated_at;
  if (at == null) return null;
  if (at instanceof Date || typeof at === 'string') return at;
  return null;
}

function partitionRows(rows: unknown): readonly RatedPartitionBound[] {
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((row) => {
    if (row === null || typeof row !== 'object') return [];
    const relname = 'relname' in row && typeof row.relname === 'string' ? row.relname : '';
    const bound = 'bound' in row && (typeof row.bound === 'string' || row.bound === null) ? row.bound : null;
    return relname === '' ? [] : [{ relname, bound }];
  });
}
